import React from "react";
import { motion } from "motion/react";
import { CheckCircle2, Sparkles, Crown, ArrowRight } from "lucide-react";
import { useSelector } from "react-redux";

const PackageGrid = ({ 
  packages, 
  onUpgrade, 
  onDowngrade, 
  units,
  selectedDuration,
  userDuration,
}) => {
  // Get current plan from Redux
  const userDetails = useSelector((state)=>state?.user?.txtconnect_ng_user);
  const subscriptionPlan = useSelector(
    (state) => state.user?.txtconnect_ng_user?.subscription_plan
  );
  const currentSubscriptionPlanDuration = userDetails?.plan_name;

  const getPackageDetails = (pkg) => {
    const detail = pkg.details?.find(
      (d) => d.name === selectedDuration
    );
    return detail || pkg.details?.[0] || {};
  };

  const getPackageName = (pkg) => {
    return pkg.name;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  // Helper function to normalize duration strings for comparison
  const normalizeDuration = (duration) => {
    if (!duration) return null;
    const normalized = duration.toLowerCase().trim();
    // Handle variations: "1 month", "1month", "1 Month", etc.
    if (normalized.includes("1 month") || normalized === "1month") return "1 month";
    if (normalized.includes("2 month") || normalized === "2months") return "2 months";
    if (normalized.includes("3 month") || normalized === "3months") return "3 months";
    if (normalized.includes("no expiry") || normalized.includes("noexpiry")) return "No Expiry";
    return normalized;
  };

  return (
    <div className="grid grid-cols-1 gap-6 mx-auto max-w-8xl md:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
      {packages.map((pkg, index) => {
        const details = getPackageDetails(pkg);
        const packageName = getPackageName(pkg);
        
        // Get the package_name from the API response
        const apiPackageName = pkg.package_name || pkg.name;
        
        // Get duration from multiple possible sources
        const currentPlanDuration = normalizeDuration(
          subscriptionPlan?.duration || 
          userDuration ||
          subscriptionPlan?.payment_duration
        );
        const selectedDurationNormalized = normalizeDuration(selectedDuration);
        const subscriptionPackageName = subscriptionPlan.name || subscriptionPlan?.package_name;
        const isCurrentPlan = subscriptionPackageName === apiPackageName && 
                              (currentSubscriptionPlanDuration 
                              ? currentSubscriptionPlanDuration === selectedDurationNormalized 
                              : selectedDurationNormalized === "1 month");     
        const isPopular = pkg.name === "Silver";
        
        // Debug log
        //console.log(`Package ${apiPackageName}: isCurrentPlan = ${isCurrentPlan}, subscriptionPlan.name = ${subscriptionPlan?.name}`);
        
        return (
          <motion.div
            key={pkg.id || pkg.package_name || index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -8, scale: 1.02 }}
            className={`group relative overflow-hidden rounded-3xl transition-all duration-500 w-full ${
              isCurrentPlan
                ? "bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-50 border-2 border-blue-500 shadow-2xl shadow-blue-200/50"
                : isPopular
                ? "bg-gradient-to-br from-white via-red-50 to-rose-50 border-2 border-red-500 shadow-2xl shadow-red-200/50 scale-105 md:scale-110"
                : "bg-white border border-gray-200 hover:border-gray-300 shadow-lg hover:shadow-xl"
            }`}
          >                      
            {/* Modern Badge Designs */}
            {isPopular && !isCurrentPlan && (
              <div className="absolute top-4 right-4 z-10">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-rose-500 rounded-full blur-md opacity-50 animate-pulse"></div>
                  <div className="relative flex gap-1.5 items-center px-3 py-1.5 bg-gradient-to-r from-red-500 to-rose-500 rounded-full shadow-lg">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                    <span className="text-xs font-bold text-white tracking-wide">POPULAR</span>
                  </div>
                </div>
              </div>
            )}
            
            {isCurrentPlan && (
              <div className="absolute top-4 right-4 z-10">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full blur-md opacity-50 animate-pulse"></div>
                  <div className="relative flex gap-1.5 items-center px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-lg">
                    <Crown className="w-3.5 h-3.5 text-white" />
                    <span className="text-xs font-bold text-white tracking-wide">ACTIVE</span>
                  </div>
                </div>
              </div>
            )}

            {/* Decorative Top Accent */}
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              isCurrentPlan
                ? "bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"
                : isPopular
                ? "bg-gradient-to-r from-red-500 via-rose-500 to-red-500"
                : "bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"
            }`}></div>

            {/* Card Content */}
            <div className="flex flex-col p-8 min-w-0">
              {/* Plan Name */}
              <div className="mb-6">
                <h3 className={`text-3xl font-extrabold mb-2 ${
                  isCurrentPlan
                    ? "text-blue-700"
                    : isPopular
                    ? "text-red-700"
                    : "text-gray-800"
                }`}>
                  {pkg.name}
                </h3>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200">
                  <span className="text-xs font-medium text-gray-600">{selectedDuration}</span>
                </div>
              </div>
              
              {/* Price Section */}
              <div className="mb-6 overflow-hidden">
                <div className="flex flex-wrap gap-1 items-baseline mb-2 w-full">
                  <span className={`text-2xl sm:text-3xl md:text-4xl font-black leading-tight w-full whitespace-nowrap ${
                    isCurrentPlan
                      ? "text-blue-600"
                      : isPopular
                      ? "text-red-600"
                      : "text-gray-800"
                  }`}>
                  GH₵{details.price}
                </span>
              </div>
              </div>

              {/* Divider */}
              <div className={`h-px w-full mb-6 ${
                isCurrentPlan
                  ? "bg-gradient-to-r from-transparent via-blue-300 to-transparent"
                  : isPopular
                  ? "bg-gradient-to-r from-transparent via-red-300 to-transparent"
                  : "bg-gradient-to-r from-transparent via-gray-300 to-transparent"
              }`}></div>
              
              {/* Features List */}
              <div className="mb-8 flex-1">
                <p className={`mb-4 text-sm font-semibold uppercase tracking-wide ${
                  isCurrentPlan
                    ? "text-blue-600"
                    : isPopular
                    ? "text-red-600"
                    : "text-gray-500"
                }`}>
                  What's Included
                </p>
                <ul className="space-y-4">
                  <li className="flex gap-3 items-start">
                    <div className={`flex-shrink-0 mt-0.5 flex justify-center items-center w-6 h-6 rounded-full ${
                      isCurrentPlan
                        ? "bg-blue-100"
                        : isPopular
                        ? "bg-red-100"
                        : "bg-gray-100"
                    }`}>
                      <CheckCircle2 className={`w-4 h-4 ${
                        isCurrentPlan
                          ? "text-blue-600"
                          : isPopular
                          ? "text-red-600"
                          : "text-gray-600"
                      }`} />
                    </div>
                    <span className={`text-base ${
                      isCurrentPlan
                        ? "text-blue-900"
                        : "text-gray-700"
                    }`}>
                      <span className="font-semibold">{formatNumber(details.unit?.trim() || 0)}</span> SMS Units
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className={`flex-shrink-0 mt-0.5 flex justify-center items-center w-6 h-6 rounded-full ${
                      isCurrentPlan
                        ? "bg-blue-100"
                        : isPopular
                        ? "bg-red-100"
                        : "bg-gray-100"
                    }`}>
                      <CheckCircle2 className={`w-4 h-4 ${
                        isCurrentPlan
                          ? "text-blue-600"
                          : isPopular
                          ? "text-red-600"
                          : "text-gray-600"
                      }`} />
                    </div>
                    <span className={`text-base ${
                      isCurrentPlan
                        ? "text-blue-900"
                        : "text-gray-700"
                    }`}>
                      Unlimited Contact Groups
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className={`flex-shrink-0 mt-0.5 flex justify-center items-center w-6 h-6 rounded-full ${
                      isCurrentPlan
                        ? "bg-blue-100"
                        : isPopular
                        ? "bg-red-100"
                        : "bg-gray-100"
                    }`}>
                      <CheckCircle2 className={`w-4 h-4 ${
                        isCurrentPlan
                          ? "text-blue-600"
                          : isPopular
                          ? "text-red-600"
                          : "text-gray-600"
                      }`} />
                    </div>
                    <span className={`text-base ${
                      isCurrentPlan
                        ? "text-blue-900"
                        : "text-gray-700"
                    }`}>
                      24/7 Customer Support
                    </span>
                  </li>
                </ul>
              </div>
              
              {/* Action Button */}
              <motion.button
                onClick={() => {
                  const packageData = { 
                    ...pkg, 
                    ...details, 
                    duration: selectedDuration,
                    name: pkg.name,
                    package_name: apiPackageName,
                    price: details.price,
                    unit: details.unit || details.units
                  };
                  // Determine if it's an upgrade or downgrade based on unit comparison
                  const currentUnits = parseInt(subscriptionPlan?.units || units || 0);
                  const newUnits = parseInt(details.unit || details.units || 0);
                  
                  if (newUnits > currentUnits) {
                    onUpgrade(packageData, pkg.name);
                  } else {
                    onDowngrade(packageData, pkg.name);
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`group relative overflow-hidden w-full py-4 px-6 rounded-2xl font-bold text-base text-center transition-all duration-300 ${
                  isCurrentPlan
                    ? "text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-200/50 hover:shadow-xl hover:shadow-blue-300/50 hover:from-blue-700 hover:to-indigo-700 opacity-80"
                    : isPopular
                    ? "text-white bg-gradient-to-r from-red-600 to-rose-600 shadow-lg shadow-red-200/50 hover:shadow-xl hover:shadow-red-300/50 hover:from-red-700 hover:to-rose-700"
                    : "text-white bg-gradient-to-r from-gray-700 to-gray-800 shadow-md hover:shadow-lg hover:from-gray-800 hover:to-gray-900"
                }`}
                // disabled={isCurrentPlan}
              >
                <span className="relative z-10 flex gap-2 items-center justify-center">
                  {isCurrentPlan ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Current Plan
                    </>
                  ) : (
                    <>
                      Choose {pkg.name}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </span>
                
                {/* Button Shine Effect */}
                {!isCurrentPlan && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    animate={{
                      x: ['-100%', '100%'],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </motion.button>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default PackageGrid;